import React, { useState, useEffect, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Building2, FileText } from 'lucide-react';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const USComplianceMap = ({ onStateClick }) => {
  const [stateData, setStateData] = useState({});
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    loadComplianceData();
    
    // Refresh data when window/tab regains focus
    const handleFocus = () => {
      loadComplianceData();
    };
    
    window.addEventListener('focus', handleFocus);
    
    // Also refresh every 30 seconds to catch updates
    const intervalId = setInterval(loadComplianceData, 30000);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, []);

  const loadComplianceData = async () => {
    try {
      // Fetch backend-computed compliance classifications
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/compliance-heatmap`);
      const data = await response.json();

      // Transform backend response to component state format
      // SINGLE SOURCE OF TRUTH: Heat Map now displays exactly what State Matrix shows
      const stateMap = {};
      Object.entries(data.states || {}).forEach(([stateCode, classification]) => {
        stateMap[stateCode] = {
          status: classification.status_color, // 'red', 'yellow', 'green', 'gray'
          label: classification.status_label,
          reason: classification.reason_summary,
          reasonCodes: classification.reason_codes || [],
          triggeringRecords: classification.triggering_records || [],
          // Use new separated counts (registrations = DBAs + Filings, SEPARATE from registered agents)
          licenses: classification.counts?.licenses || 0,
          registrations: classification.counts?.registrations || 0,  // DBAs + Filings only
          registeredAgents: classification.counts?.registered_agents || 0,  // Separate count
          // Legacy compatibility fields
          ancillaryRegistrations: classification.counts?.ancillary_registrations || 0,
          dbas: classification.counts?.dbas || 0,
          filings: classification.counts?.filings || 0,
          requirements: classification.counts?.requirements || 0,
          upcomingRenewals: classification.counts?.upcoming_renewals || 0,
          deficiencies: classification.counts?.critical_issues || 0,
          // New detailed fields
          requiredLicenses: classification.required_licenses || [],
          missingItems: classification.missing_items || [],
          criticalDetails: classification.critical_details || [],
          upcomingItems: classification.upcoming_items || [],
          counts: classification.counts || {},
          // Texas HB 2503 foreign registration exception
          hb2503Exception: classification.hb2503_exception || null
        };
      });

      setStateData(stateMap);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
    } finally {
      setLoading(false);
    }
  };


  const getStateCodeFromName = (geoName) => {
    const nameToCode = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    };
    return nameToCode[geoName] || null;
  };

  const getStateColor = (status) => {
    switch (status) {
      case 'red': return '#ef4444';
      case 'yellow': return '#f59e0b';
      case 'green': return '#10b981';
      case 'gray':
      default: return '#e5e7eb';
    }
  };

  const handleMouseMove = (e, geo) => {
    const stateCode = getStateCodeFromName(geo.properties.name);
    if (!stateCode || !mapContainerRef.current) return;

    // Get the map container's bounding box (not individual geography element)
    const rect = mapContainerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const relativeY = e.clientY - rect.top;
    
    setTooltipPosition({
      x: relativeX,
      y: relativeY
    });
    setHoveredState(stateCode);
  };

  const handleMouseLeave = () => {
    setHoveredState(null);
  };

  const handleClick = (geo) => {
    const stateCode = getStateCodeFromName(geo.properties.name);
    if (stateCode && onStateClick) {
      onStateClick(stateCode, stateData[stateCode]);
    }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="relative">
      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold">U.S. Compliance Heat Map</h2>
          <p className="text-muted-foreground mt-1">Real-time regulatory status across all jurisdictions</p>
        </div>

        <div className="flex items-center gap-6 mb-4 pb-4 border-b flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
            <span className="text-sm">Fully Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
            <span className="text-sm">Action Required (within 60 days)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div>
            <span className="text-sm">Critical - Not Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-muted"></div>
            <span className="text-sm">Not Licensed</span>
          </div>
        </div>

        <div ref={mapContainerRef} className="relative bg-muted/10 rounded-lg p-4">
          <ComposableMap projection="geoAlbersUsa">
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateCode = getStateCodeFromName(geo.properties.name);
                  const data = stateCode ? stateData[stateCode] : null;
                  const color = data ? getStateColor(data.status) : '#e5e7eb';

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={color}
                      stroke="#374151"
                      strokeWidth={0.75}
                      style={{
                        default: { outline: 'none' },
                        hover: { 
                          fill: color,
                          opacity: 0.85,
                          filter: 'brightness(1.15)',
                          outline: 'none',
                          cursor: 'pointer'
                        },
                        pressed: { outline: 'none' }
                      }}
                      onMouseMove={(e) => handleMouseMove(e, geo)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleClick(geo)}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {hoveredState && stateData[hoveredState] && (() => {
            // Simple, reliable positioning: tooltip appears to the right and below cursor
            const tooltipWidth = 300;
            const offset = 20;
            
            // Get container dimensions from ref
            const containerWidth = mapContainerRef.current?.offsetWidth || 900;
            const containerHeight = mapContainerRef.current?.offsetHeight || 600;
            
            // Position to right of cursor by default
            let left = tooltipPosition.x + offset;
            let top = tooltipPosition.y + offset;
            
            // If tooltip would overflow right edge, flip to left side
            if (left + tooltipWidth > containerWidth - offset) {
              left = tooltipPosition.x - tooltipWidth - offset;
            }
            
            // Keep within bounds
            left = Math.max(offset, Math.min(left, containerWidth - tooltipWidth - offset));
            top = Math.max(offset, Math.min(top, containerHeight - 150)); // Approximate tooltip height
            
            return (
              <div
                className="absolute z-50 bg-background border-2 border-border rounded-lg shadow-xl p-4 pointer-events-none"
                style={{
                  left: `${left}px`,
                  top: `${top}px`,
                  minWidth: '280px',
                  maxWidth: '320px'
                }}
              >
                <div className="font-semibold text-lg mb-2 flex items-center justify-between">
                  <span>{getStateName(hoveredState)}</span>
                  <Badge variant={
                    stateData[hoveredState].status === 'red' ? 'destructive' :
                    stateData[hoveredState].status === 'yellow' ? 'default' :
                    stateData[hoveredState].status === 'green' ? 'success' :
                    'outline'
                  } data-testid="state-status-badge">
                    {stateData[hoveredState].label}
                  </Badge>
                </div>
                
                {/* Current Counts - SINGLE SOURCE OF TRUTH from compliance endpoint */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Active Licenses
                    </span>
                    <span className="font-medium">{stateData[hoveredState].licenses}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Registrations
                    </span>
                    <span className="font-medium">{stateData[hoveredState].registrations || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Registered Agents
                    </span>
                    <span className="font-medium">{stateData[hoveredState].registeredAgents || 0}</span>
                  </div>
                </div>
                
                <div className="text-xs text-muted-foreground mt-3 pt-2 border-t">
                  Click to view full compliance details
                </div>
              </div>
            );
          })()}
        </div>
      </Card>
    </div>
  );
};

const getStateName = (code) => {
  const names = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };
  return names[code] || code;
};

export default USComplianceMap;
